import * as React from 'react'
import { Animated, Text, TouchableOpacity, View } from 'react-native'
import styled from '@theme/styled-components'

import Icon from '@components/icon/Icons'
import DocumentButton, { BUTTON_HEIGHT } from '@components/button/DocumentButton'

//import useI18n from '@i18n/useI18n'
import useNavigation from '@layout/useNavigation'

import useBreakpoint from '@utils/useBreakpoints'

import theme from '@theme/Theme'

const MAIN_VERTICAL_PADDING = 16

const ANIM_DURATION = 500
const SMALL_HEADER_HEIGHT = 65

interface Props {
  displayDocuments: boolean
  hasScrolled: boolean
  onBack?: () => void
  onMapPressed: () => void
  tenant: LocatairePermanentResponse
  edlList: EtatDesLieuxResponse[]
}

const TenantHeader = ({ displayDocuments, hasScrolled, onBack, onMapPressed, tenant, edlList }: Props) => {
  // const i18n = useI18n()
  const navigation = useNavigation()
  const [{ width }] = useBreakpoint()

  const [initHeight, setInitHeight] = React.useState(0)

  const nameOpacityAnim = React.useRef(new Animated.Value(0)).current
  const contentOpacityAnim = React.useRef(new Animated.Value(1)).current
  const heightAnim = React.useRef(new Animated.Value(0)).current

  React.useEffect(() => {
    // Small tenant name appears
    Animated.parallel([
      Animated.timing(nameOpacityAnim, {
        toValue: hasScrolled ? 1 : 0,
        duration: ANIM_DURATION,
        useNativeDriver: true,
      }),
      Animated.timing(contentOpacityAnim, {
        toValue: hasScrolled ? 0 : 1,
        duration: ANIM_DURATION / 2,
        useNativeDriver: true,
      }),
      Animated.timing(heightAnim, {
        toValue: hasScrolled ? SMALL_HEADER_HEIGHT : initHeight,
        duration: ANIM_DURATION,
        useNativeDriver: false,
      }),
    ]).start()
  }, [hasScrolled, initHeight])

  const onDocuments = () => {
    //TODO: not implmented
    navigation.push('/documents', {
      documents: { edl: {}, quality: [] },
      isManifestation: false,
      id: tenant.id,
      edlList: edlList,
    })
  }

  const onEmail = () => {
    //TODO: not implmented
    // Linking.openURL(`mailto:${tenant.email}`)
  }

  const onPhone = () => {
    //TODO: not implmented
    // Linking.openURL(`tel:${tenant.phoneNumber}`)
  }

  return (
    <GlobalContainer hasScrolled={hasScrolled}>
      <SafeContainer hasScrolled={hasScrolled}>
        <Container
          hasScrolled={hasScrolled}
          style={initHeight > 0 ? { height: heightAnim as unknown as number } : undefined}
          onLayout={(e) => {
            if (initHeight === 0) {
              heightAnim.setValue(e.nativeEvent.layout.height)
              setInitHeight(e.nativeEvent.layout.height)
            }
          }}>
          <TopContainer>
            <BackButton activeOpacity={0.9} onPress={onBack}>
              <Icon name="arrow-left" size={35} color={theme.colors.background} />
            </BackButton>
            <SmallName style={{ opacity: nameOpacityAnim }} numberOfLines={1} ellipsizeMode="tail">
              {tenant.nomLocatairePermanent}
            </SmallName>
            {/* TODO : conditionner à la présence d'un plan*/}
            {true ? (
              <MapButton activeOpacity={0.9} onPress={onMapPressed}>
                <Icon name="map" size={20} color={theme.colors.primary} />
              </MapButton>
            ) : (
              <DummyView size={35} />
            )}
          </TopContainer>

          <Content style={{ opacity: contentOpacityAnim, transform: [{ scaleY: contentOpacityAnim }] }}>
            <Name numberOfLines={2} ellipsizeMode="tail">
              {tenant.nomLocatairePermanent}
            </Name>
            {/*//TODO: not implemented*/}
            {/*<TenantDate>*/}
            {/*  {i18n.t(`screens.tenants.${tenant.status}Date`, {*/}
            {/*    date: new Date(tenant.status === 'former' ? tenant.endDate : tenant.startDate),*/}
            {/*  })}*/}
            {/*</TenantDate>*/}
            <Separator />
            <Contact>
              <ContactName>{`${tenant.nomLocatairePermanent}`}</ContactName>
              {/* TODO : not implemented, conditionner à la présence d'un email/tel de contact */}
              {false && (
                <ContactButton activeOpacity={0.9} onPress={onEmail}>
                  <Icon name="email" size={24} color={theme.colors.background} />
                </ContactButton>
              )}
              {false && (
                <ContactButton activeOpacity={0.9} onPress={onPhone}>
                  <Icon name="phone" size={24} color={theme.colors.background} />
                </ContactButton>
              )}
            </Contact>
          </Content>
        </Container>
      </SafeContainer>
      {!!displayDocuments && (
        <AlertButtonContainer
          style={{ width, opacity: contentOpacityAnim, transform: [{ scaleY: contentOpacityAnim }] }}>
          <ButtonBackground>
            <DocumentButton count={3} onClick={onDocuments} />
          </ButtonBackground>
        </AlertButtonContainer>
      )}
    </GlobalContainer>
  )
}

export default TenantHeader
const GlobalContainer = styled(View)<{ hasScrolled: boolean }>`
  ${(props) => !props.hasScrolled && `padding-bottom: ${BUTTON_HEIGHT / 2}px;`}
`
const SafeContainer = styled(View)<{ hasScrolled: boolean }>`
  z-index: 2;
  background-color: ${(props) => props.theme.colors.primary};
  padding-top: ${(props) => props.theme.insets.top}px;
`
const Container = styled(Animated.View)<{ hasScrolled: boolean }>`
  z-index: 2;
  background-color: ${(props) => props.theme.colors.primary};
  ${(props) => !props.hasScrolled && `padding-bottom: ${BUTTON_HEIGHT / 2 + MAIN_VERTICAL_PADDING}px;`}
`
const TopContainer = styled(View)`
  flex-direction: row;
  align-items: center;
  padding: ${MAIN_VERTICAL_PADDING}px;
`
const BackButton = styled(TouchableOpacity)`
  flex-direction: row;
  align-items: center;
`
const Name = styled(Text)`
  ${(props) => props.theme.fonts.Title1};
  color: ${(props) => props.theme.colors.background};
  text-align: center;
  padding: 7px 0;
`
/*
const TenantDate = styled(Text)`
  ${(props) => props.theme.fonts.Caption1};
  color: ${(props) => props.theme.colors.background};
  text-align: center;
  margin-bottom: 16px;
`
*/
const AlertButtonContainer = styled(Animated.View)`
  padding: ${MAIN_VERTICAL_PADDING}px;
  position: absolute;
  left: 0;
  bottom: 0;
  padding-bottom: 0;
  z-index: 3;
`
const ButtonBackground = styled(View)`
  border-radius: 10px;
  background-color: ${(props) => props.theme.colors.primary};
`
const Separator = styled(View)`
  height: 1px;
  background-color: ${(props) => props.theme.colors.disable};
  margin-bottom: 16px;
`
const Content = styled(Animated.View)`
  justify-content: center;
  padding: 0 ${MAIN_VERTICAL_PADDING}px;
`
const Contact = styled(View)`
  flex-direction: row;
  align-items: center;
`
const ContactName = styled(Text)`
  ${(props) => props.theme.fonts.Subheadline};
  color: ${(props) => props.theme.colors.background};
  flex: 1;
`
const ContactButton = styled(TouchableOpacity)`
  padding: 0 8px;
`
const SmallName = styled(Animated.Text)`
  ${(props) => props.theme.fonts.Headline};
  color: ${(props) => props.theme.colors.background};
  text-align: center;
  flex: 1;
`
const DummyView = styled(View)<{ size: number }>`
  height: ${(props) => props.size}px;
  width: ${(props) => props.size}px;
`
const MapButton = styled(TouchableOpacity)`
  height: 32px;
  width: 32px;
  border-radius: 36px;
  align-items: center;
  justify-content: center;
  background-color: ${(props) => props.theme.colors.background};
`
